import React, { Fragment } from 'react';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import Project from '../../api/project';
import { useParams } from 'react-router-dom';
import Link from 'shared/SlashLink';
import PageTitle from '../../components/pagetitle/PageTitle'
import Scrollbar from '../../components/scrollbar/scrollbar'
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import icon from '../../images/icons/icon_check_3.svg'

const CATEGORY_LABELS = {
    'managed-it': 'Managed IT',
    cybersecurity: 'Cybersecurity',
    cloud: 'Cloud & Infrastructure',
    ai: 'AI Integration',
    web: 'Web & Digital',
};

const PortfolioSinglePage = () => {
    const { slug } = useParams();
    const PortfolioDetails = Project.find(item => item.slug === slug);

    usePageSeo({
        title: PortfolioDetails?.title || 'Portfolio',
        description: PortfolioDetails?.description
            || `Case study from BUILDLAB's portfolio of affordable housing and business technology engagements.`,
    });

    const ClickHandler = () => {
        window.scrollTo(10, 0);
    };

    if (!PortfolioDetails) {
        return (
            <Fragment>
                <Header />
                <main id="main-content" className="page_content">
                    <PageTitle pageTitle={'Project not found'} pagesub={'Details '} pageTop={'Portfolio'} />
                    <section className="section_space bg-light">
                        <div className="container text-center">
                            <p className="mb-0">We couldn&rsquo;t find that project. <Link onClick={ClickHandler} to="/portfolio">Browse all projects</Link>.</p>
                        </div>
                    </section>
                    <Footer />
                    <Scrollbar />
                </main>
            </Fragment>
        );
    }

    const overview = (PortfolioDetails.overview && PortfolioDetails.overview.length)
        ? PortfolioDetails.overview
        : [PortfolioDetails.description];
    const highlights = PortfolioDetails.highlights || [];
    const outcome = PortfolioDetails.outcome || [];

    // Related projects: prefer same category, then fill from the rest.
    const sameCat = Project.filter(p => p.slug !== slug && p.category === PortfolioDetails.category);
    const others = Project.filter(p => p.slug !== slug && p.category !== PortfolioDetails.category);
    const related = [...sameCat, ...others].slice(0, 2);

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content about-page">
                <PageTitle pageTitle={PortfolioDetails.title} pagesub={'Details '} pageTop={'Portfolio'} />
                <section className="portfolio_details_section section_space bg-light">
                    <div className="container">
                        <div className="details_item_image">
                            <img src={PortfolioDetails.pImg} alt={`BUILDLAB — ${PortfolioDetails.title}`} />
                        </div>
                        <h2 className="details_item_title">{PortfolioDetails.title}</h2>
                        {overview.map((para, i) => (
                            <p key={i} className={i === overview.length - 1 ? 'mb-0' : ''}>{para}</p>
                        ))}
                        <hr />
                        <ul className="portfolio_details_info_list icon_list unordered_list justify-content-lg-between mb-5">
                            <li>
                                <span className="icon_list_text">
                                    <strong className="text-dark text-uppercase">Service:</strong>
                                    {PortfolioDetails.services || PortfolioDetails.sub}
                                </span>
                            </li>
                            <li>
                                <span className="icon_list_text">
                                    <strong className="text-dark text-uppercase">Sector:</strong>
                                    {PortfolioDetails.thumb2}
                                </span>
                            </li>
                            <li>
                                <span className="icon_list_text">
                                    <strong className="text-dark text-uppercase">Category:</strong>
                                    {CATEGORY_LABELS[PortfolioDetails.category] || PortfolioDetails.sub}
                                </span>
                            </li>
                            {PortfolioDetails.timeframe && (
                                <li>
                                    <span className="icon_list_text">
                                        <strong className="text-dark text-uppercase">Timeframe:</strong>
                                        {PortfolioDetails.timeframe}
                                    </span>
                                </li>
                            )}
                        </ul>

                        {PortfolioDetails.external && (
                            <p className="mb-5">
                                <a className="btn btn-primary" href={PortfolioDetails.external} target="_blank" rel="noopener noreferrer">
                                    <span className="btn_label" data-text="Visit the live site">Visit the live site</span>
                                    <span className="btn_icon">
                                        <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
                                    </span>
                                </a>
                            </p>
                        )}

                        {highlights.length > 0 && (
                            <Fragment>
                                <h3 className="details_item_info_title pt-4">What we delivered</h3>
                                <div className="row mb-4">
                                    {[0, 1].map(col => (
                                        <div className="col-lg-6" key={col}>
                                            <ul className="icon_list unordered_list_block">
                                                {highlights.filter((_, idx) => idx % 2 === col).map((h, idx) => (
                                                    <li key={idx}>
                                                        <span className="icon_list_icon">
                                                            <img src={icon} alt="Check SVG Icon" />
                                                        </span>
                                                        <span className="icon_list_text">{h}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </Fragment>
                        )}

                        {outcome.length > 0 && (
                            <Fragment>
                                <h3 className="details_item_info_title pt-4">Outcome</h3>
                                {outcome.map((para, i) => (
                                    <p key={i} className={i === outcome.length - 1 ? 'mb-0' : ''}>{para}</p>
                                ))}
                            </Fragment>
                        )}

                        {related.length > 0 && (
                            <Fragment>
                                <h3 className="details_item_info_title pt-5 mb-4">Related projects</h3>
                                <div className="row">
                                    {related.map((project, prj) => (
                                        <div className="col-lg-6" key={prj}>
                                            <div className="portfolio_block portfolio_layout_2">
                                                <div className="portfolio_image">
                                                    <Link onClick={ClickHandler} className="portfolio_image_wrap bg-light" to={`/portfolio-details/${project.slug}`}>
                                                        <img src={project.pImg} alt={`BUILDLAB — ${project.title}`} />
                                                    </Link>
                                                </div>
                                                <div className="portfolio_content">
                                                    <h3 className="portfolio_title">
                                                        <Link onClick={ClickHandler} to={`/portfolio-details/${project.slug}`}>
                                                            {project.title}
                                                        </Link>
                                                    </h3>
                                                    <ul className="category_list unordered_list">
                                                        <li><Link onClick={ClickHandler} to={`/portfolio-details/${project.slug}`}><i className="fa-solid fa-tags" aria-hidden="true"></i> {project.thumb1}</Link></li>
                                                        <li><Link onClick={ClickHandler} to={`/portfolio-details/${project.slug}`}><i className="fa-solid fa-building" aria-hidden="true"></i> {project.thumb2}</Link></li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Fragment>
                        )}
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};
export default PortfolioSinglePage;
